import {Component, forwardRef, OnInit} from '@angular/core';
import { NG_VALUE_ACCESSOR, Validators, FormControl, FormGroup, FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {BusinessOverdraftRateCardValue, compareMatch, MinMaxRateValue} from '@portal-workspace/grow-shared-library';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {UntilDestroy} from '@ngneat/until-destroy';
import { Mark } from '@portal-workspace/grow-ui-library/mark';
import {setupUntilDestroy} from '@portal-workspace/grow-ui-library';
import {Subscription} from 'rxjs';
import {delay, distinctUntilChanged, tap} from 'rxjs/operators';
import {requiredAllowEmptyValidator} from '@portal-workspace/grow-ui-library';
import {MARK} from '@portal-workspace/grow-ui-library/mark';
import { MinMaxRateComponent } from './min-max-rate.component';
import { MatDividerModule } from '@angular/material/divider';
import { InputMaskModule } from '@ngneat/input-mask';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

import {
  createTwoDecimalInputMask,
  formControlErrorKeys,
  formControlErrorMessage,} from '../component-utils';

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    selector: 'business-overdraft-rate-card',
    templateUrl: './business-overdraft-rate-card.component.html',
    styleUrls: ['./business-overdraft-rate-card.component.scss'],
    exportAs: 'businessOverdraftRateCardComponent',
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => BusinessOverdraftRateCardComponent), multi: true },
        { provide: MARK, useExisting: forwardRef(() => BusinessOverdraftRateCardComponent), multi: false },
    ],
    standalone: true,
  imports: [MatFormFieldModule, MatInputModule, FormsModule, InputMaskModule, ReactiveFormsModule, MatDividerModule, MinMaxRateComponent]
})
export class BusinessOverdraftRateCardComponent extends AbstractControlValueAccessor<BusinessOverdraftRateCardValue> implements OnInit, Mark {

  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;

  createTwoDecimalInputMask = createTwoDecimalInputMask();

  subscriptions: Subscription[] = [];


  formGroup: FormGroup<{
    tier1Rate: FormControl<number | null>,
    tier2Rate: FormControl<number | null>,
    tier3Rate: FormControl<number | null>,
    facilityFee: FormControl<number | null>,
    docFee: FormControl<MinMaxRateValue | null>,
    rbaRate: FormControl<number | null>,
  }>;

  formControlTier1Rate: FormControl<number | null>;
  formControlTier2Rate: FormControl<number | null>;
  formControlTier3Rate: FormControl<number | null>;
  formControlFacilityFee: FormControl<number | null>;
  formControlRbaRate: FormControl<number | null>;
  formControlDocFee: FormControl<MinMaxRateValue>;

  constructor(private formBuilder: FormBuilder) {
    super();
    this.formControlTier1Rate = formBuilder.control(0, [Validators.required]);
    this.formControlTier2Rate = formBuilder.control(0, [Validators.required]);
    this.formControlTier3Rate = formBuilder.control(0, [Validators.required]);
    this.formControlFacilityFee = formBuilder.control(0, [Validators.required]);
    this.formControlDocFee = formBuilder.control([], [requiredAllowEmptyValidator]);
    this.formControlRbaRate = formBuilder.control(0);
    this.formGroup = formBuilder.group({
      tier1Rate: this.formControlTier1Rate,
      tier2Rate: this.formControlTier2Rate,
      tier3Rate: this.formControlTier3Rate,
      facilityFee: this.formControlFacilityFee,
      docFee: this.formControlDocFee,
      rbaRate: this.formControlRbaRate,
    });
  }


  ngOnInit(): void {
    setupUntilDestroy(this);
    const sub = this.formGroup.valueChanges.pipe(
      delay(0),
      distinctUntilChanged(compareMatch),
      tap(r => {
        if (this.formGroup.valid) {
          const v: BusinessOverdraftRateCardValue = {
            tier1Rate: r.tier1Rate ?? 0,
            tier2Rate: r.tier2Rate ?? 0,
            tier3Rate: r.tier3Rate ?? 0,
            facilityFee: r.facilityFee ?? 0,
            docFee: r.docFee ?? [],
            rbaRate: r.rbaRate ?? 0,
          };
          this.propagateChange(v);
        } else {
          this.propagateChange(null);
        }
      })
    ).subscribe();
    this.subscriptions.push(sub);
  }

  doWriteValue(v: BusinessOverdraftRateCardValue | undefined): void | BusinessOverdraftRateCardValue {
    if (v) {
      this.formControlTier1Rate.setValue(v.tier1Rate);
      this.formControlTier2Rate.setValue(v.tier2Rate);
      this.formControlTier3Rate.setValue(v.tier3Rate);
      this.formControlFacilityFee.setValue(v.facilityFee);
      this.formControlDocFee.setValue(v.docFee ?? []);
      this.formControlRbaRate.setValue(v.rbaRate);
    }
    return undefined;
  }

  mark() {
    this.formGroup.markAllAsTouched();
  }
}
